import NiceModal, { useModal } from '@ebay/nice-modal-react';

import { useCallback, useEffect, useState } from 'react';

import Button from 'components/field/Button';
import Modal from 'components/global/Modal';
import Typography from 'components/global/Typography';
import { ModalActions, ModalContent, ModalTitle } from 'components/global/styled/Modal.styled';
import Maps from 'components/maps/Maps';
import { getCityCoordinates } from 'components/maps/utils/mapHelpers';

import AppLogger from 'utils/AppLogger';

import { MapContainer } from './CityGroupForm.styled';

const PolygonSelectionModal = NiceModal.create((props) => {
	const { viewMode, locationArray, index, setValue, cityName } = props;
	const modal = useModal();
	const [map, setMap] = useState(null);
	const maps = window?.google?.maps;
	const [polygonData, setPolygonData] = useState(locationArray[index].polygon);
	const [customMapSettings, setCustomMapSettings] = useState(null);

	useEffect(() => {
		const fetchMapSettings = async () => {
			try {
				const cityCoordinates = await getCityCoordinates(cityName);
				setCustomMapSettings(cityCoordinates);
			} catch (error) {
				AppLogger.error(error);
			}
		};

		if (cityName) {
			fetchMapSettings();
		}
	}, [cityName]);

	const VerticesToStringArray = (vertices) => {
		const verticesArray = [];
		vertices.forEach((vertex, index) => {
			const x = vertex.lat(index);
			const y = vertex.lng(index);
			verticesArray.push(`${x} ${y}`);
		});
		if (verticesArray.length && verticesArray[0] !== verticesArray[verticesArray.length - 1]) {
			verticesArray.push(verticesArray[0]);
		}
		return `(${verticesArray.toString()})`;
	};

	const initPolygon = async (coordinates) => {
		const bounds = new maps.LatLngBounds();
		let coords = null;
		if (typeof coordinates === 'string') {
			const coordArr = coordinates.split(',');
			coords = coordArr.map((coord) => {
				const [lat, lng] = coord.trim().split(' ');
				return { lat: parseFloat(lat), lng: parseFloat(lng) };
			});
		}
		const { Polygon } = await maps.importLibrary('maps');

		const area = new Polygon({
			paths: coords ? coords : coordinates,
			clickable: !viewMode,
			draggable: !viewMode,
			editable: !viewMode,
			fillColor: '#ee8888',
			fillOpacity: 0.35,
			strokeColor: '#ee8888',
			strokeOpacity: 1.0,
			strokeWeight: 2,
			zIndex: 1
		});
		area.setMap(map);

		coordinates.forEach((item) => {
			bounds.extend({ lat: parseFloat(item.lat), lng: parseFloat(item.lng) });
		});
		map.fitBounds(bounds);

		const vertices = area.getPath();
		setPolygonData(VerticesToStringArray(vertices));

		maps.event.addListener(vertices, 'set_at', () => {
			setPolygonData(VerticesToStringArray(vertices));
		});
		maps.event.addListener(vertices, 'insert_at', () => {
			setPolygonData(VerticesToStringArray(vertices));
		});
	};
	const initMap = async () => {
		const { DrawingManager } = await maps.importLibrary('drawing');

		const drawingManager = new DrawingManager({
			drawingMode: maps.drawing.OverlayType.POLYGON,
			drawingControl: true,
			drawingControlOptions: {
				position: maps.ControlPosition.TOP_CENTER,
				drawingModes: [maps.drawing.OverlayType.POLYGON]
			},
			polygonOptions: {
				draggable: true,
				editable: true,
				fillColor: '#ee8888',
				fillOpacity: 0.35,
				strokeColor: '#ee8888',
				strokeOpacity: 1.0,
				strokeWeight: 2,
				zIndex: 1
			}
		});
		drawingManager.setMap(map);

		maps.event.addListener(drawingManager, 'overlaycomplete', (polygon) => {
			drawingManager.setDrawingMode(null);
			drawingManager.drawingControl = false;
			drawingManager.setMap(null);

			const path = polygon.overlay.getPath();
			const polygonArray = path.getArray();
			const coordinates = polygonArray.map((coordinate) => ({
				lat: coordinate.lat(),
				lng: coordinate.lng()
			}));
			path.clear();
			initPolygon(coordinates);
		});
	};
	useEffect(() => {
		if (map) {
			maps.event.addListenerOnce(map, 'idle', () => {
				if (polygonData) {
					if (typeof polygonData === 'string') {
						const trimmedCoordinates = polygonData.replace(/^\((.*?)\)$/, '$1');

						const coordArr = trimmedCoordinates.split(',');
						const latLngArr = coordArr.map((coord) => {
							const [lat, lng] = coord.trim().split(' ');
							return { lat: parseFloat(lat), lng: parseFloat(lng) };
						});
						initPolygon(latLngArr);
					} else if (Array.isArray(polygonData)) {
						initPolygon(polygonData);
					}
				} else {
					initMap();
				}
			});
		}
	}, [map, polygonData]);

	const handleCloseModal = useCallback(() => {
		modal.remove();
	}, []);

	const handleSave = () => {
		setValue(`locationArray.${index}.polygon`, polygonData, { shouldValidate: true, shouldDirty: true });
		handleCloseModal();
	};

	return (
		<Modal fullwidth maxWidth="medium" name="polygon-selection-modal" closeButton={false}>
			<ModalTitle>
				<Typography variant="p1">Select Polygon</Typography>
			</ModalTitle>
			<ModalContent>
				<MapContainer key={customMapSettings?.lat}>
					<Maps setMap={setMap} customMapSettings={customMapSettings} />
				</MapContainer>
			</ModalContent>
			<ModalActions>
				<Button label="cancel" variant="outline" onClick={handleCloseModal} />
				<Button label={'save'} onClick={handleSave} iconPos="right" />
			</ModalActions>
		</Modal>
	);
});

export default PolygonSelectionModal;
